import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Headset, BadgeCheck, Users, Sparkles } from "lucide-react";
import { Hero } from "@/components/Hero";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { PackageCard } from "@/components/PackageCard";
import { Testimonials } from "@/components/Testimonials";
import { Newsletter } from "@/components/Newsletter";
import { Reveal, SectionHeading } from "@/components/Reveal";
import api from "@/lib/api";
import { formatINR, waLink, SITE } from "@/lib/site";
import { GoogleG } from "@/components/GoogleG";

const DESTINATIONS = [
  { name: "Kerala", from: 24999, img: "https://images.unsplash.com/photo-1624554305378-0f440dd3a8c1?auto=format&fit=crop&w=900&q=80", q: "Kerala" },
  { name: "Kashmir", from: 21999, img: "https://images.unsplash.com/photo-1595815771614-ade9d652a65d?auto=format&fit=crop&w=900&q=80", q: "Kashmir" },
  { name: "Bali", from: 64999, img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=900&q=80", q: "Bali" },
  { name: "Dubai", from: 54999, img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=900&q=80", q: "Dubai" },
  { name: "Europe", from: 145999, img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=900&q=80", q: "Paris" },
];

const TRUST = [
  { n: "01", icon: Award, title: "15+ years on the road", text: "Exchanging happiness since 2011 — long enough to know every shortcut and every sunset spot." },
  { n: "02", icon: Users, title: "12,000+ happy travellers", text: "Families, honeymooners and corporate groups who come back for trip two, three and four." },
  { n: "03", icon: Headset, title: "24/7 human support", text: "A real trip manager on WhatsApp from booking to touchdown. No bots, no waiting rooms." },
  { n: "04", icon: BadgeCheck, title: "Best price guarantee", text: "Find the same itinerary cheaper and we match it. Transparent quotes, zero hidden costs." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [igPosts, setIgPosts] = useState([]);
  const [google, setGoogle] = useState(null);

  useEffect(() => {
    api.get("/packages", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/instagram/feed").then((r) => setIgPosts(r.data.data || [])).catch(() => {});
    api.get("/reviews/google").then((r) => { if (r.data.configured && !r.data.error) setGoogle(r.data); }).catch(() => {});
  }, []);

  const reviewItems = google?.reviews?.length
    ? google.reviews.map((r) => ({ ...r, trip: "Google review" }))
    : testimonials;
  const liveRating = google?.rating ? Number(google.rating).toFixed(1) : "4.8";

  return (
    <div>
      <Hero />
      <EditorialMarquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="destinations-section">
        <div className="flex items-end justify-between gap-6 mb-12">
          <SectionHeading overline="Top Picks" title={<>Where to, <span className="font-light italic">next?</span></>} />
          <Link to="/packages" data-testid="view-all-packages-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-sun transition-colors shrink-0">
            View all packages <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
          {DESTINATIONS.map((d, i) => (
            <Reveal key={d.name} delay={i * 0.08} className={i === 0 ? "col-span-2 md:col-span-1" : ""}>
              <Link to={`/packages?search=${encodeURIComponent(d.q)}`} data-testid={`destination-${d.name.toLowerCase()}`} className="group block">
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                  <img src={d.img} alt={`${d.name} tour packages`} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <p className="text-sun text-[10px] uppercase tracking-[0.25em] mb-1">Explore</p>
                    <p className="text-bone font-display text-2xl font-black">{d.name}</p>
                    <p className="text-bone/70 text-xs mt-1">Starts @ {formatINR(d.from)} /person</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sand/40 py-24" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between gap-6 mb-12">
            <SectionHeading overline="Featured Packages" title={<>Trips travellers <span className="font-light italic">love.</span></>} />
            <Link to="/packages" data-testid="featured-view-all"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold hover:text-sun transition-colors shrink-0">
              View all <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.slice(0, 6).map((pkg, i) => (
              <Reveal key={pkg.slug} delay={(i % 3) * 0.1}>
                <PackageCard pkg={pkg} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-bone py-28 grain relative overflow-hidden" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading dark overline="The click2trip Manifesto" title={<>Why 12,000+ travellers <span className="font-light italic text-bone/70">trust us.</span></>} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {TRUST.map((t, i) => (
              <Reveal key={t.n} delay={i * 0.1}>
                <div className="flex gap-6 items-start" data-testid={`trust-${t.n}`}>
                  <span className="font-display text-5xl font-black text-outline shrink-0">{t.n}</span>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <t.icon size={20} className="text-sun" />
                      <h3 className="font-display text-xl font-bold">{t.title}</h3>
                    </div>
                    <p className="text-bone/60 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden" data-testid="cta-band">
        <img src="https://images.pexels.com/photos/25245176/pexels-photo-25245176.jpeg" alt="Mountain expedition" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="text-sun text-xs uppercase tracking-[0.3em] mb-4">Limited period · Monsoon Sale</p>
            <h2 className="font-display text-4xl sm:text-6xl font-black text-bone leading-tight max-w-3xl mx-auto">
              Save up to 25% on your dream trip
            </h2>
            <p className="text-bone/70 mt-4 mb-8">Let our experts curate it for you — free of charge.</p>
            <a href={waLink("Hi click2trip! I'd like to know about the Monsoon Sale offers.")} target="_blank" rel="noreferrer"
              data-testid="cta-expert-btn"
              className="inline-flex items-center gap-2 bg-sun text-bone font-semibold px-9 py-4 rounded-full hover:scale-105 active:scale-95 transition-transform">
              <Sparkles size={16} /> Connect with an Expert
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <SectionHeading dark overline="Google Reviews" title={<>{liveRating}<span className="text-sun">★</span> on Google</>} />
            <div className="flex items-center gap-4">
              <p className="text-bone/50 text-sm hidden sm:block" data-testid="google-ratings-count">
                {google?.total ? `${google.total} ratings on Google` : "Real reviews from Google"}
              </p>
              <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="google-review-btn"
                className="inline-flex items-center gap-2 bg-bone text-ink text-sm font-semibold px-5 py-2.5 rounded-full hover:scale-105 transition-transform">
                <GoogleG size={16} /> Review us on Google
              </a>
            </div>
          </div>
          <Testimonials items={reviewItems} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="gallery-strip">
        <div className="flex items-end justify-between gap-6 mb-12">
          <SectionHeading overline="Instagram" title={<>Follow <span className="font-light italic text-sun">@click_2_trip</span></>} />
          <a href="https://instagram.com/click_2_trip" target="_blank" rel="noreferrer" data-testid="instagram-follow-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold hover:text-sun transition-colors shrink-0">
            Follow us <ArrowRight size={15} />
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="instagram-grid">
          {(igPosts.length
            ? igPosts.map((p) => ({ image: p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url, caption: (p.caption || "click2trip on Instagram").slice(0, 120), link: p.permalink }))
            : gallery.map((g) => ({ ...g, link: "https://instagram.com/click_2_trip" }))
          ).slice(0, 4).map((g, i) => (
            <Reveal key={g.link || g.caption} delay={i * 0.06}>
              <a href={g.link} target="_blank" rel="noreferrer" data-testid={`instagram-post-${i}`}
                className={`block relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}>
                <img src={g.image} alt={g.caption} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-500" />
                <p className="absolute bottom-3 left-4 right-4 text-bone text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500 line-clamp-2">{g.caption}</p>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink py-24" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-sun mb-4">The Sunday Postcard</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-bone mb-4 max-w-xl">Deals & destinations in your inbox</h2>
            <p className="text-bone/60 mb-8 max-w-lg">One email a week. New packages, member-only prices and travel guides worth reading. No spam, ever.</p>
            <Newsletter />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
